/* EQ State — inline variant toggle for collection cards.
   Swaps the hidden variant id on the card's add-to-cart form, plus the card
   image, price and button state, without navigating or opening a modal.
   Rendered only for single-option products with 2-3 variants (see
   snippets/card-product.liquid); everything else keeps Dawn's quick-add modal. */
if (!customElements.get('variant-toggle')) {
  customElements.define(
    'variant-toggle',
    class VariantToggle extends HTMLElement {
      connectedCallback() {
        this.options = Array.from(this.querySelectorAll('.eqx-vt__opt'));
        if (!this.options.length) return;

        this.card = this.closest('.card-wrapper');
        this.options.forEach((option) => {
          option.addEventListener('click', () => this.select(option));
        });
        this.addEventListener('keydown', (event) => this.onKeydown(event));
      }

      get form() {
        // Resolved lazily: the <form> is a later sibling, so it may not exist yet
        // when connectedCallback runs during streaming parse.
        return document.getElementById(this.dataset.formId);
      }

      select(option) {
        if (option.hasAttribute('disabled')) return;
        if (option.getAttribute('aria-checked') === 'true') return;

        this.options.forEach((each) => {
          const isSelected = each === option;
          each.setAttribute('aria-checked', String(isSelected));
          each.setAttribute('tabindex', isSelected ? '0' : '-1');
        });

        this.updateForm(option);
        this.updateImage(option);
        this.updatePrice(option);
      }

      updateForm(option) {
        const form = this.form;
        if (!form) return;

        const input = form.querySelector('.product-variant-id');
        const available = option.dataset.available === 'true';

        if (input) {
          input.value = option.dataset.variantId;
          input.disabled = !available;
        }

        const submit = form.querySelector('[name="add"]');
        if (!submit) return;

        submit.disabled = !available;
        const label = submit.querySelector('span:not(.sold-out-message)');
        if (label) {
          label.textContent = available ? this.dataset.labelAdd : this.dataset.labelSoldout;
        }
      }

      updateImage(option) {
        if (!this.card || !option.dataset.img) return;

        const image = this.card.querySelector('.card__media img');
        if (!image) return;

        image.src = option.dataset.img;
        if (option.dataset.srcset) image.srcset = option.dataset.srcset;
      }

      updatePrice(option) {
        // Deliberately conservative: only the plain regular price is rewritten,
        // and only when it actually differs. Every Format pair is currently
        // priced identically, so this is a guard against a future price split
        // rather than something that fires today. Sale markup is left alone.
        if (!this.card || !option.dataset.price) return;

        const priceContainer = this.card.querySelector('.price');
        if (!priceContainer || priceContainer.classList.contains('price--on-sale')) return;

        const priceItem = priceContainer.querySelector('.price-item--regular');
        if (!priceItem) return;
        if (priceItem.textContent.trim() === option.dataset.price) return;

        priceItem.textContent = option.dataset.price;
      }

      onKeydown(event) {
        const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
        if (!keys.includes(event.key)) return;

        const current = this.options.findIndex((option) => option.getAttribute('aria-checked') === 'true');
        if (current === -1) return;

        const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
        const step = forward ? 1 : -1;

        // Walk past disabled options so keyboard users can't land on a dead one.
        for (let hop = 1; hop <= this.options.length; hop += 1) {
          const count = this.options.length;
          const index = (((current + step * hop) % count) + count) % count;
          const candidate = this.options[index];
          if (candidate && !candidate.hasAttribute('disabled')) {
            event.preventDefault();
            this.select(candidate);
            candidate.focus();
            return;
          }
        }
      }
    }
  );
}
